import type { ReactElement } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { LoginPage } from './auth/LoginPage';
import { ReauthModal } from './auth/ReauthModal';
import { SessionProvider, useSession } from './auth/session-context';
import { AppLayout } from './components/AppLayout';
import { LiveEventsProvider } from './live/live-events';
import { AuditPage } from './routes/AuditPage';
import { BacktestPage } from './routes/BacktestPage';
import { ControlPage } from './routes/ControlPage';
import { DecisionsPage } from './routes/DecisionsPage';
import { OverviewPage } from './routes/OverviewPage';
import { PositionsPage } from './routes/PositionsPage';
import { StrategyPage } from './routes/StrategyPage';

/**
 * Routing and the authentication gate.
 *
 * The panel is never rendered without a session — not even in a local network,
 * per the spec. While the session is being resolved the app shows a neutral
 * loading screen rather than a flash of the login form, and when it turns out
 * to be gone the login screen explains that the session expired.
 */

function LoadingScreen(): ReactElement {
  return (
    <main className="auth-page">
      <p className="empty-state" role="status">
        Проверка сессии…
      </p>
    </main>
  );
}

function NotFoundPage(): ReactElement {
  return (
    <>
      <div className="page-head">
        <h1>Раздел не найден</h1>
      </div>
      <p className="empty-state">
        Такой страницы нет. <Link to="/">Вернуться на обзор</Link>.
      </p>
    </>
  );
}

/** Routes for an authenticated operator. Exported for tests. */
export function AuthenticatedApp(): ReactElement {
  return (
    <LiveEventsProvider>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<OverviewPage />} />
          <Route path="strategy" element={<StrategyPage />} />
          <Route path="positions" element={<PositionsPage />} />
          <Route path="decisions" element={<DecisionsPage />} />
          <Route path="control" element={<ControlPage />} />
          <Route path="backtest" element={<BacktestPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <ReauthModal />
    </LiveEventsProvider>
  );
}

export function AppShell(): ReactElement {
  const { status } = useSession();
  if (status === 'loading') return <LoadingScreen />;
  if (status === 'anonymous') return <LoginPage />;
  return <AuthenticatedApp />;
}

export function App(): ReactElement {
  return (
    <SessionProvider>
      <AppShell />
    </SessionProvider>
  );
}
